// The page an attendee's own phone loads after scanning a stand's code.
//
// PUBLIC `/j/` ONLY. Onsite is a different URL (`/s/`, OnsiteApp) and a hall
// tablet. Putting the onsite form on this URL — "skip the code, they are at
// the stand" — is how a photographed QR becomes a form-first public
// registration. The console's capture offer is public | onsite | both; this
// file is what public means.
//
// WHAT THIS FILE IS ALLOWED TO DECIDE, which is almost nothing. Every decision
// with a consequence lives somewhere a check can read it: the stage ordering in
// flow.ts, what may be stored in privacy/storage.ts, whether the notices may
// render at all in privacy/notices.ts, whether the token is stripped in
// measurement.ts. This file arranges them on a screen. That division is the
// codebase's rule about the browser deciding nothing security-relevant, applied
// to a surface where the browser is a stranger's phone.
//
// THE ORDER OF THE FIRST TWO CALLS IS LOAD-BEARING AND IS NOT ARBITRARY. The
// token comes out of the address bar before React exists, because anything that
// runs first is something that could read `location.href`. See measurement.ts.

import { StrictMode, useCallback, useEffect, useMemo, useState } from "react";
import { createRoot } from "react-dom/client";

import { DEFAULT_WHEEL_COPY } from "@nova/xp-visitor";
import "@nova/xp-ui/tokens.css";
import "@nova/xp-ui/ui.css";
import "./web.css";

import type { FieldDefinition } from "../../ui/src/fields/types.js";
import { BUILD } from "./build/config.js";
import { readEnvironment } from "./build/environment.js";
import { Checking } from "./checking.js";
import {
  askForCode,
  fetchConsentWording,
  mayFollow,
  serverFor,
  spendCode,
  submitRegistration,
  type Refusal,
  type Stage,
  type Transport,
} from "./flow.js";
import { syncPublicJoinPath } from "./link.js";
import { recordMeasuredPage, takeTokenFromTheAddressBar, type PublicJoin } from "./measurement.js";
import { OnsiteApp } from "./onsiteApp.js";
import { Page } from "./page.js";
import { WheelAfter } from "./wheelAfter.js";
import { visitorLogoFrom, WheelFrame } from "./wheelFrame.js";
import {
  readConsentWording,
  readNoticeConfig,
  type AttestationDocument,
  type ConsentDocument,
  type ConsentWordingReading,
} from "./privacy/notices.js";
import { globalPrivacyControlIsSet } from "./privacy/storage.js";
import {
  legalDocumentsForPage,
  RegistrationBottom,
  RegistrationSteps,
  registrationPageCount,
  type FormPage,
  type SubmittedDetails,
} from "./registrationForm.js";
import { CodeBoxes } from "./codeBoxes.js";
import { applyClientTokens, applyTheme, preferredTheme, type PageTheme } from "./theme.js";

// BEFORE ANYTHING ELSE, INCLUDING BEFORE THE MODULE GRAPH FINISHES SETTLING.
// A one-time token may sit in a url where xp/console's session token may not,
// and the argument for that is in measurement.ts — but the argument depends on
// the token being gone from the address bar by the time anything could read it,
// and "anything" includes a measurement script somebody adds later without
// reading this comment.
const arrival = takeTokenFromTheAddressBar(window.location, window.history);

// Read from the build-time constants vite substitutes, and typed here rather
// than reached for through import.meta so that this file compiles under plain
// tsc as well as under vite. buildConfig.ts is the one place that knows the
// shape of what the bundler injected.
const environment = readEnvironment(BUILD.environment);
const server = serverFor(environment);
applyTheme(preferredTheme());

/**
 * The service's address, or null for a build that names none.
 *
 * At module scope rather than inside the component, because the wording read
 * below is an effect and an effect cannot sit after a conditional return. React
 * requires every hook on every render, and a component that returned early for a
 * missing server before running its effects would break that rule the day the
 * server was missing — which is the one day nobody tests.
 */
const baseUrl = "baseUrl" in server ? server.baseUrl : null;

/**
 * fetch, wrapped to the shape flow.ts wants, with the two options that matter.
 *
 * `credentials: "omit"` because this surface has no cookies and must keep not
 * having any: a default of same-origin would silently start sending one the day
 * somebody adds it, and the cookie notice would be wrong before anybody noticed.
 *
 * `referrerPolicy: "no-referrer"` restates the meta tag in index.html. Two locks
 * on the same door, on the kiosk's rule, and this one covers the case where the
 * meta tag is stripped by a transform and the header is the only thing left.
 */
const transport: Transport = async (url, init) => {
  const response = await fetch(url, {
    method: init.method,
    headers: init.headers,
    // Spread rather than `body: init.body`, because exactOptionalPropertyTypes
    // makes those two different things and fetch means the difference: a `body`
    // key holding undefined is still a body on a GET, which fetch refuses.
    ...(init.body === undefined ? {} : { body: init.body }),
    credentials: "omit",
    referrerPolicy: "no-referrer",
  });
  return { status: response.status, json: () => response.json() };
};

function Refused({ refusal, onRestart }: { refusal: Refusal; onRestart: () => void }) {
  return (
    <section aria-live="assertive">
      <h2>That did not work</h2>
      {/* The server's own words. Rewriting them here would be two sets of
          wording to keep true, and the server's is the one that knows why. */}
      <p>{refusal.problem}</p>
      <button type="button" className="xp-btn" onClick={onRestart}>
        Start again
      </button>
    </section>
  );
}

function App({ activationId, join }: { activationId: string; join: PublicJoin }) {
  const [theme, setTheme] = useState<PageTheme>(() => preferredTheme());
  const [stage, setStage] = useState<Stage>(() =>
    activationId === ""
      ? {
          at: "unusable",
          problem:
            "This link does not name a stand, so there is nothing to register for. " +
            "Scan the code on the stand again, or ask somebody there to register you.",
        }
      : { at: "start" },
  );
  const [busy, setBusy] = useState(false);
  const [spinTicket, setSpinTicket] = useState<string | null>(null);

  // EVERY TRANSITION GOES THROUGH THE TABLE, not through whichever setState the
  // handler felt like calling. The ordering ruling — code first, then the form —
  // is a row in TRANSITIONS, and a component that could bypass it would make the
  // table a comment. A refused transition throws rather than being ignored,
  // because a page that silently stayed put would look like a slow network.
  const go = useCallback((next: Stage) => {
    setStage((current) => {
      if (!mayFollow(current.at, next.at)) {
        throw new Error(
          `refusing to move from ${current.at} to ${next.at}: flow.ts does not permit it. ` +
            `If this is a transition the surface should have, it belongs in TRANSITIONS with a reason.`,
        );
      }
      return next;
    });
  }, []);

  const notices = useMemo(() => readNoticeConfig(BUILD.notices), []);

  /**
   * This stand's consent wording, or null while it is being asked for.
   *
   * NULL IS A THIRD STATE AND NOT A CONVENIENCE. "Still asking" and "asked and
   * refused" are different facts, and collapsing them would mean the page
   * rendered the refusal screen for the length of a round trip on every load —
   * which on venue wifi is long enough to read, so every attendee would be told
   * the stand cannot register them and then watch it change its mind.
   */
  const [wording, setWording] = useState<ConsentWordingReading | null>(null);

  useEffect(() => {
    if (wording?.ok === true) applyClientTokens(theme, wording.theme, wording.themeDark);
    else applyTheme(theme);
  }, [theme, wording]);

  // ASKED ONCE, BEFORE ANYTHING IS ASKED OF THE ATTENDEE. See
  // fetchConsentWording for why this is the page's first request rather than
  // something done at the form stage: everything it can refuse is a fact about
  // the stand, so it is knowable before a person is involved, and discovering it
  // after somebody has left the page to fetch a code out of their email is
  // making them do that for nothing.
  //
  // `live` rather than an AbortController, because there is nothing to abort
  // usefully — the request is a read of public text and cancelling it saves the
  // server nothing — and what actually needs guarding is the setState, which
  // StrictMode will otherwise run twice in development and warn about.
  useEffect(() => {
    if (baseUrl === null || activationId === "") return undefined;

    let live = true;

    void (async () => {
      const got = await fetchConsentWording(transport, baseUrl, activationId);
      // The server's refusal is passed straight through, which for this route is
      // one sentence covering a stand that is shut, one that has finished, one
      // from another environment and one that never existed. Those four are
      // deliberately indistinguishable — api/src/attend/wording.ts is the
      // argument — and a page that tried to be more specific would be putting
      // back the distinction the server removed.
      const reading: ConsentWordingReading = got.ok
        ? await readConsentWording(got.served)
        : { ok: false, problem: got.refusal.problem };
      if (live) setWording(reading);
    })();

    return () => {
      live = false;
    };
  }, [activationId]);

  useEffect(() => {
    if (activationId === "" || join === null) return;
    syncPublicJoinPath(window.location, window.history, activationId, join);
  }, [activationId, join]);

  useEffect(() => {
    const at = stage.at;
    if (at === "form" || at === "writing") return;
    recordMeasuredPage("public", at, activationId);
  }, [stage.at, activationId]);

  // The path, not the stand. Both public paths are live on an activation at
  // once — `/j/` is the plain registration and `/w/` is the same one with a
  // spin at the end — so the stand's join kind cannot say which of the two the
  // person in front of us scanned. Only the address they opened can.
  //
  // ABOVE `shell` BECAUSE `shell` READS IT. Every early return below — the
  // build with no server, and the wait while the stand's wording arrives —
  // calls `shell`, so a declaration further down is a temporal dead zone the
  // first render walks into, and the page renders nothing at all.
  const wheelJoin = join === "wheel";

  const shell = (children: React.ReactNode) => (
    <Page theme={theme} onTheme={setTheme} webFooter>
      {children}
    </Page>
  );

  if (baseUrl === null) {
    // Cannot happen in a shipped build — vite.config.ts refuses one with no
    // server — and is rendered rather than thrown anyway.
    return shell(
      <section>
        <h1>Not available</h1>
        <p>{"problem" in server ? server.problem : "This build names no server."}</p>
      </section>,
    );
  }

  // THE NOTICES GATE THE WHOLE SURFACE, INCLUDING THE ADDRESS FIELD.
  //
  // Not a footer that can be missing. readNoticeConfig refuses to produce
  // anything until somebody has ruled on who the data controller is, and the
  // consequence of that refusal is this screen: no address is asked for, because
  // asking for one means telling somebody who is going to hold it, and this
  // deployment cannot say.
  // THE SECOND GATE, WHICH NOW OPENS FOR A CORRECTLY CONFIGURED STAND AND IS
  // STILL A GATE. Until the consent-wording route existed this always closed,
  // because the page could not obtain the client's approved paragraph and
  // therefore could not hash what it displayed. It now asks for it, and it
  // refuses on exactly the same terms whenever the answer will not do: a stand
  // that is not open, wording whose hash does not match its own text, a stand
  // with no published configuration at all, a bundle citing nothing this page can
  // present. See readConsentWording.
  //
  // IT IS NO LONGER THE ONLY THING REFUSING, which is the point of the change
  // that added `config_version` and is worth knowing while reading this gate. A
  // stand with nothing published serves no version, the register route requires
  // one, and so a page that skipped this gate would now be refused by the server
  // rather than quietly filing a registration with no configuration behind it.
  // This screen is what turns that refusal into a sentence somebody can act on
  // before an attendee has typed anything.
  //
  // Refusing to register anybody remains the only honest option when that
  // happens, and it is the recoverable one: nobody is registered without a
  // basis, and the page starts working the moment the stand is right.
  if (wording === null) {
    return shell(
      <Checking />,
    );
  }

  if (!wording.ok) {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This registration page is not finished being set up, so it is not collecting anything. Please
          ask somebody on the stand.
        </p>
        <p role="note">{wording.problem}</p>
      </section>,
    );
  }

  if (wording.captureOffer === "onsite") {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This stand is taking registrations on site, not from a visitor&apos;s own phone. Ask somebody
          on the stand to register you there.
        </p>
      </section>,
    );
  }

  if (!notices.ok) {
    return shell(
      <section>
        <h1>Not available</h1>
        <p>
          This registration page is not finished being set up, so it is not collecting anything. Please
          ask somebody on the stand.
        </p>
        {/* The operator's sentence, not the attendee's. Visible rather than in a
            console, because the person who can fix it is standing next to the
            person seeing it. */}
        <p role="note">{notices.problem}</p>
      </section>,
    );
  }

  // Spread into request bodies rather than sent as null, so a page that somehow
  // reached here without a path says nothing and the server falls back to the
  // stand's join kind, as it did before the two paths ran side by side.
  const lane = join === null ? {} : { lane: join };
  const wheelLogo = visitorLogoFrom(wording.logos, theme);

  if (stage.at === "done" && wheelJoin && spinTicket !== null) {
    return shell(
      <WheelAfter
        activationId={activationId}
        ticket={spinTicket}
        slices={wording.wheelSlices}
        lane="web"
        mode={theme}
        transport={transport}
        baseUrl={baseUrl}
        logo={wheelLogo}
        onHome={() => {
          window.location.reload();
        }}
      />,
    );
  }

  const stageBody =
    stage.at === "unusable" ? (
      <section>
        <h1>Nothing to register for</h1>
        <p>{stage.problem}</p>
      </section>
    ) : stage.at === "refused" ? (
      <Refused refusal={stage} onRestart={() => go({ at: "address" })} />
    ) : stage.at === "done" ? (
      <section aria-live="polite">
        <h1>Thank You.</h1>
        <p className="web-lede">
          You are registered. There is nothing else to do, and this page has not kept anything on your
          phone.
        </p>
      </section>
    ) : stage.at === "start" ? (
      <StartStage
        onStart={() => go({ at: "address" })}
        wheel={wheelJoin}
      />
    ) : stage.at === "address" ? (
      <AddressStage
        busy={busy}
        of={registrationPageCount(wording.fields, 2)}
        onSubmit={async (email) => {
          setBusy(true);
          const refusal = await askForCode(transport, baseUrl, {
            activation_id: activationId,
            email,
            ...lane,
          });
          setBusy(false);
          go(refusal === null ? { at: "code", email } : { at: "refused", ...refusal });
        }}
      />
    ) : stage.at === "code" ? (
      <CodeStage
        email={stage.email}
        busy={busy}
        of={registrationPageCount(wording.fields, 2)}
        onBack={() => go({ at: "address" })}
        onResend={async () => {
          setBusy(true);
          const refusal = await askForCode(transport, baseUrl, {
            activation_id: activationId,
            email: stage.email,
            ...lane,
          });
          setBusy(false);
          if (refusal !== null) go({ at: "refused", ...refusal });
        }}
        onSubmit={async (code) => {
          setBusy(true);
          const outcome = await spendCode(transport, baseUrl, {
            activation_id: activationId,
            email: stage.email,
            code,
          });
          setBusy(false);
          go(
            outcome.ok
              ? { at: "form", email: stage.email, proof: outcome.proof }
              : { at: "refused", ...outcome.refusal },
          );
        }}
      />
    ) : (
      <FormStage
        activationId={activationId}
        email={stage.email}
        busy={busy || stage.at === "writing"}
        consent={wording.documents}
        attestations={wording.attestations}
        privacyNotice={wording.privacyNotice}
        fields={wording.fields}
        foldDisplayName={wording.foldDisplayName}
        legal={legalDocumentsForPage(wording.legalDocuments, notices.documents)}
        onSubmit={async (details) => {
          go({ at: "writing", email: stage.email, proof: stage.proof });
          const outcome = await submitRegistration(transport, baseUrl, {
            activation_id: activationId,
            ticket: stage.proof.ticket,
            email: stage.email,
            ...lane,
            display_name: details.displayName === "" ? null : details.displayName,
            phone: details.phone === "" ? null : details.phone,
            // The version the paragraphs on this screen came out of, not
            // whatever is live now. readConsentWording refuses a response with
            // no version, so reaching this line means there is one to cite.
            config_version: wording.configVersion,
            field_set_version: wording.fieldSetVersion,
            fields: details.fields,
            consent: details.consent,
          });
          if (outcome.ok) {
            setSpinTicket(outcome.written.spin_ticket ?? null);
            go({ at: "done" });
            return;
          }
          go({ at: "refused", ...outcome.refusal });
        }}
      />
    );

  if (wheelJoin && stage.at !== "unusable" && stage.at !== "refused") {
    return shell(
      <WheelFrame slices={wording.wheelSlices} mode={theme} lane="web" logo={wheelLogo}>
        {stageBody}
      </WheelFrame>,
    );
  }

  return shell(
    <>
      {stageBody}
      <LegalLinks />
    </>,
  );
}

function StartStage({
  onStart,
  wheel,
}: {
  onStart: () => void;
  wheel: boolean;
}) {
  return (
    <section className="web-welcome">
      {wheel ? <h1>{DEFAULT_WHEEL_COPY.heading}</h1> : null}
      {wheel ? <p>{DEFAULT_WHEEL_COPY.body}</p> : null}
      <div className="xp-acts">
        <button type="button" className="xp-btn xp-btn--primary" onClick={onStart}>
          {wheel ? DEFAULT_WHEEL_COPY.attract : "Start"}
        </button>
      </div>
    </section>
  );
}

function AddressStage({
  busy,
  of,
  onSubmit,
}: {
  busy: boolean;
  of: number;
  onSubmit: (email: string) => void;
}) {
  const [email, setEmail] = useState("");
  return (
    <section className="web-form">
      <h1>
        To confirm it&apos;s you, we&apos;ll send a one-time code to your email. You&apos;ll need to
        enter it to continue.
      </h1>
      <p className="web-lede">Nothing is collected about you until you confirm it.</p>
      <form
        className="web-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) onSubmit(email.trim());
        }}
      >
        <label htmlFor="email" className="sr">
          Email address
        </label>
        <input
          id="email"
          name="email"
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="Enter Your Email"
          required
          value={email}
          onChange={(event) => setEmail(event.target.value)}
        />
        <button
          type="submit"
          className="xp-btn xp-btn--primary"
          disabled={busy || !/\S+@\S+/.test(email.trim())}
          aria-busy={busy}
        >
          {busy ? "Sending…" : "Submit"}
        </button>
      </form>
      <RegistrationBottom step={1} of={of} />
    </section>
  );
}

function CodeStage({
  email,
  busy,
  of,
  onBack,
  onResend,
  onSubmit,
}: {
  email: string;
  busy: boolean;
  of: number;
  onBack: () => void;
  onResend: () => void;
  onSubmit: (code: string) => void;
}) {
  const [code, setCode] = useState("");
  const [resent, setResent] = useState(false);
  return (
    <section className="web-form">
      <h1>We&apos;ve sent a one-time passcode to {email}.</h1>
      <p className="web-lede">
        If that address can receive mail, there is now a message with a link and a six-digit code.
        Either one finishes this.
      </p>
      <form
        className="web-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy) onSubmit(code.trim());
        }}
      >
        {/* Six real inputs, same as the Claude kit. A clipped 1px field behind
            painted boxes is what iPad would not type or paste into. `one-time-code`
            on the first box is what lets Mail offer the digits without switching
            apps; see api/src/auth/delivery.ts. */}
        <CodeBoxes
          id="code"
          value={code}
          disabled={busy}
          onValue={setCode}
          onComplete={(next) => {
            if (!busy) onSubmit(next);
          }}
        />
        <p className="web-linkrow">
          <button type="button" onClick={onBack} disabled={busy}>
            Change Email Address
          </button>
          <span aria-hidden="true">|</span>
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setResent(true);
              onResend();
            }}
          >
            Resend Code
          </button>
        </p>
        {resent ? (
          <p className="web-lede" aria-live="polite">
            Another code is on its way to {email}.
          </p>
        ) : null}
        <button
          type="submit"
          className="xp-btn xp-btn--primary"
          disabled={busy || code.trim().length !== 6}
          aria-busy={busy}
        >
          {busy ? "Checking…" : "Next"}
        </button>
      </form>
      <RegistrationBottom step={2} of={of} onBack={onBack} disabled={busy} />
    </section>
  );
}

function FormStage({
  activationId,
  email,
  busy,
  consent,
  attestations,
  privacyNotice,
  fields,
  foldDisplayName,
  legal,
  onSubmit,
}: {
  activationId: string;
  email: string;
  busy: boolean;
  consent: readonly ConsentDocument[];
  attestations: readonly AttestationDocument[];
  privacyNotice: string | null;
  fields: readonly FieldDefinition[];
  foldDisplayName: boolean;
  legal: readonly import("../../ui/src/legal/documents.js").AgreementDocument[];
  onSubmit: (details: SubmittedDetails) => void;
}) {
  const [formStep, setFormStep] = useState<FormPage>("details");
  useEffect(() => {
    recordMeasuredPage("public", formStep, activationId);
  }, [formStep, activationId]);
  return (
    <RegistrationSteps
      lede={
        <p className="web-lede">
          Registering as <strong>{email}</strong>. That address is confirmed and cannot be changed here.
        </p>
      }
      busy={busy}
      consent={consent}
      attestations={attestations}
      privacyNotice={privacyNotice}
      fields={fields}
      legal={legal}
      keepMailbox={false}
      foldDisplayName={foldDisplayName}
      onSubmit={onSubmit}
      step={formStep}
      onStep={setFormStep}
      leadIn={2}
    />
  );
}

function LegalLinks() {
  return (
    <footer>
      {globalPrivacyControlIsSet(navigator as { globalPrivacyControl?: unknown }) ? (
        <p>
          Your browser is sending a Global Privacy Control signal. This page sells and shares nothing
          and stores nothing on your device beyond what is needed to complete this registration. The
          signal means this visit is not counted.
        </p>
      ) : null}
    </footer>
  );
}

// Named for the surface, on the rule the other three follow — console, screen,
// station. This one kept the bundler's default while index.html was written to
// say "page", and the two disagreeing is not a degraded render: the throw below
// happens at module scope, so nothing paints at all and the browser shows a
// white rectangle with the refusal it was meant to display sitting unreachable
// in the bundle. check-builds.mjs now asserts the two agree.
const MOUNT_ID = "page";

const mount = document.getElementById(MOUNT_ID);
if (mount === null) {
  throw new Error(`index.html has no #${MOUNT_ID}, so the build that produced this bundle is broken`);
}

createRoot(mount).render(
  <StrictMode>
    {arrival.lane === "onsite" ? (
      <OnsiteApp
        activationId={arrival.activationId ?? ""}
        transport={transport}
        wheelJoin={arrival.join === "wheel"}
      />
    ) : (
      <App activationId={arrival.activationId ?? ""} join={arrival.join} />
    )}
  </StrictMode>,
);
